import { Component } from "@angular/core";
import {
    Router,
    NavigationStart,
    NavigationCancel,
    NavigationEnd,
} from "@angular/router";
import { filter } from "rxjs/operators";
import { AnalyticsService } from "./shared/services/analytics.service";
import { LanguageService } from "./shared/services/language.service";
declare var WOW: any;

declare let gtag: Function;

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
})
export class AppComponent {
    location: string = "/";
    private lastUrl = "";
    private modalQueryParam = "project";
    private langQueryParam = "lang";

    private hasTrackedFirstPage = false;

    constructor(
        private router: Router,
        private analytics: AnalyticsService,
        _language: LanguageService,
    ) {
        this.router.events
            .pipe(filter((e) => e instanceof NavigationEnd))
            .subscribe((ev: NavigationEnd) => {
                this.location = ev.url;
                const prev = this.lastUrl || "";
                const curr = ev.urlAfterRedirects;

                const onlyModalParamChanged = this.isOnlyModalQueryChange(
                    prev,
                    curr,
                    this.modalQueryParam,
                );

                const onlyLangParamChanged = this.isOnlyLangQueryChange(
                    prev,
                    curr,
                );

                const onlyFragmentChanged = this.isOnlyFragmentChange(
                    prev,
                    curr,
                );

                const isInitialLoad = !this.hasTrackedFirstPage;

                console.debug("[Router]", {
                    curr,
                    isInitialLoad,
                    onlyFragmentChanged,
                    onlyModalParamChanged,
                    onlyLangParamChanged,
                });

                if (this.analytics.suppressNextPageView) {
                    this.analytics.suppressNextPageView = false;
                    this.lastUrl = curr;
                    return;
                }

                if (onlyFragmentChanged) {
                    this.lastUrl = curr;
                    return;
                }

                if (onlyModalParamChanged && !isInitialLoad) {
                    this.lastUrl = curr;
                    return;
                }

                if (onlyLangParamChanged && !isInitialLoad) {
                    this.lastUrl = curr;
                    return;
                }

                // Count real page views
                this.analytics.trackPageView({
                    page_path: curr,
                    page_location: location.href,
                    page_title: document.title,
                });

                this.hasTrackedFirstPage = true;
                this.lastUrl = curr;
            });

        this.lastUrl = this.router.url;
    }

    private normalizePath(path: string) {
        return path.replace(/\/+$/, "") || "/";
    }

    private parseUrl(url: string) {
        const base = location.origin;

        try {
            const u = new URL(url, base);

            const params = new URLSearchParams(u.search);
            const list: string[] = [];

            params.forEach((value, key) => {
                list.push(`${key}=${value}`);
            });

            list.sort();

            return {
                pathname: this.normalizePath(u.pathname),
                paramsString: list.join("&"),
                hash: u.hash || "",
            };
        } catch {
            return {
                pathname: this.normalizePath(
                    url.split("?")[0].split("#")[0] || "",
                ),
                paramsString: "",
                hash: url.split("#")[1] ? "#" + url.split("#")[1] : "",
            };
        }
    }

    private isOnlyFragmentChange(prevUrl: string, currUrl: string): boolean {
        if (!prevUrl || !currUrl) return false;
        const p = this.parseUrl(prevUrl);
        const c = this.parseUrl(currUrl);
        return (
            p.pathname === c.pathname &&
            p.paramsString === c.paramsString &&
            p.hash !== c.hash
        );
    }

    private isOnlyModalQueryChange(
        prevUrl: string,
        currUrl: string,
        modalParam: string,
    ): boolean {
        const p = this.parseUrl(prevUrl);
        const c = this.parseUrl(currUrl);

        if (p.pathname !== c.pathname) return false;

        // reconstruct param maps excluding modalParam
        const getParamsMap = (url: string) => {
            const base = location.origin;
            const map: Record<string, string[]> = {};
            try {
                const sp = new URL(url, base).searchParams;
                sp.forEach((v, k) => {
                    if (!map[k]) map[k] = [];
                    map[k].push(v);
                });
            } catch {
                // ignore
            }
            return map;
        };

        const prevMap = getParamsMap(prevUrl);
        const currMap = getParamsMap(currUrl);

        delete prevMap[modalParam];
        delete currMap[modalParam];

        // compare remaining params keys+values
        const keysPrev = Object.keys(prevMap).sort();
        const keysCurr = Object.keys(currMap).sort();
        if (keysPrev.length !== keysCurr.length) return false;
        for (let i = 0; i < keysPrev.length; i++) {
            if (keysPrev[i] !== keysCurr[i]) return false;
            const a = (prevMap[keysPrev[i]] || []).slice().sort().join("|");
            const b = (currMap[keysCurr[i]] || []).slice().sort().join("|");
            if (a !== b) return false;
        }

        const hadModalBefore = new URL(
            prevUrl,
            location.origin,
        ).searchParams.has(modalParam);
        const hasModalNow = new URL(currUrl, location.origin).searchParams.has(
            modalParam,
        );
        return (
            hadModalBefore !== hasModalNow ||
            (hadModalBefore &&
                hasModalNow &&
                new URL(prevUrl, location.origin).searchParams.get(
                    modalParam,
                ) !==
                    new URL(currUrl, location.origin).searchParams.get(
                        modalParam,
                    ))
        );
    }

    private isOnlyLangQueryChange(prevUrl: string, currUrl: string): boolean {
        if (!prevUrl || !currUrl || prevUrl === currUrl) return false;
        const p = this.parseUrl(prevUrl);
        const c = this.parseUrl(currUrl);
        if (p.pathname !== c.pathname || p.hash !== c.hash) return false;
        if (
            this.paramsStringExcluding(prevUrl, this.langQueryParam) !==
            this.paramsStringExcluding(currUrl, this.langQueryParam)
        ) {
            return false;
        }
        return true;
    }

    private paramsStringExcluding(url: string, excludeKey: string): string {
        const base = location.origin;
        try {
            const u = new URL(url, base);
            const list: string[] = [];
            u.searchParams.forEach((value, key) => {
                if (key !== excludeKey) {
                    list.push(`${key}=${value}`);
                }
            });
            list.sort();
            return list.join("&");
        } catch {
            return "";
        }
    }

    ngAfterViewInit() {
        if (typeof WOW !== "undefined") {
            new WOW().init();
        }
    }
}
